/**
 * Ignition tag-type strings surfaced by the proxy's tag_readings table.
 * Keep this set in sync with the
 * Go discriminator so that the UI and server agree on which column of
 * tag_readings a reading lives in.
 */
export type TagType =
  | 'Int1'
  | 'Int2'
  | 'Int4'
  | 'Int8'
  | 'Float4'
  | 'Float8'
  | 'Boolean'
  | 'String'
  | 'Text'
  | 'DateTime'
  | 'Int1Array'
  | 'Int2Array'
  | 'Int4Array'
  | 'Int8Array'
  | 'Float4Array'
  | 'Float8Array'
  | 'BooleanArray'
  | 'StringArray'
  | 'DateTimeArray'
  | 'ByteArray'
  | 'DataSet'
  | 'Document'

/**
 * The scalar numeric tag types. `agg` and `deviation` conditions are only
 * meaningful against these — booleans, strings, timestamps, arrays, and
 * datasets are stored faithfully but cannot be aggregated.
 *
 * Keep in lockstep with the `NumericTagTypes` set on the Go side.
 */
export const NUMERIC_TAG_TYPES: ReadonlySet<TagType> = new Set<TagType>([
  'Int1',
  'Int2',
  'Int4',
  'Int8',
  'Float4',
  'Float8'
])

/** Reports whether agg/deviation conditions are valid for the given tag type. */
export function isNumericTagType(t: string | undefined | null): boolean {
  return !!t && NUMERIC_TAG_TYPES.has(t as TagType)
}

/** Live eq/ne may compare the tag as a boolean (`value_bool`). */
export function isBooleanTagType(t: string | undefined | null): boolean {
  return t === 'Boolean'
}

/** Live eq/ne may compare the tag as a string (`value_string`). Text is stored the same way. */
export function isStringTagType(t: string | undefined | null): boolean {
  return t === 'String' || t === 'Text'
}

/**
 * Returns the populated value for a reading in its native JS type, or
 * `null` when the reading carries no value (bad quality, unknown type).
 * Numeric tags return `number`, booleans return `boolean`, strings and
 * datetimes return `string`, and arrays/datasets/documents return the
 * parsed JSON payload.
 */
export function readingDisplayValue(r: TagReading): unknown {
  if (r.valueDouble !== null) return r.valueDouble
  if (r.valueInt !== null) return r.valueInt
  if (r.valueBool !== null) return r.valueBool
  if (r.valueString !== null) return r.valueString
  if (r.valueTimestamp !== null) return r.valueTimestamp
  if (r.valueJson !== null) return r.valueJson
  return null
}

/**
 * Renders a reading as a short human-readable string, suitable for chips,
 * tooltips, and tables. Respects the tag type so that booleans render as
 * `true`/`false`, timestamps as locale strings, and arrays/datasets as
 * truncated JSON.
 */
export function formatReadingDisplay(r: TagReading | undefined | null): string {
  if (!r) return '—'
  const v = readingDisplayValue(r)
  if (v === null || v === undefined) return '—'
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return String(v)
    const rounded = Math.round(v * 10000) / 10000
    return String(rounded)
  }
  if (typeof v === 'boolean') return v ? 'true' : 'false'
  if (r.tagType === 'DateTime' && typeof v === 'string') {
    const t = new Date(v)
    return Number.isNaN(t.getTime()) ? v : t.toLocaleString()
  }
  if (typeof v === 'string') return v
  const json = stringifyTagValue(v)
  return json.length > 80 ? json.slice(0, 77) + '…' : json
}

/**
 * Stringifies a non-null tag value of any shape. Objects and arrays (JSON,
 * Document, DataSet, array tags) render as JSON — never `[object Object]`.
 */
export function stringifyTagValue(v: unknown): string {
  if (typeof v === 'string') return v
  if (v !== null && typeof v === 'object') {
    try {
      const json = JSON.stringify(v)
      if (json !== undefined) return json
    } catch {
      // fall through (e.g. circular structure)
    }
  }
  return String(v)
}

/**
 * A single persisted reading from tag_readings.
 *
 * Exactly one of `valueDouble`, `valueInt`, `valueBool`, `valueString`,
 * `valueTimestamp`, or `valueJson` is populated per reading when `isGood`
 * is true; the populated field is dictated by `tagType`.
 *
 * `value` is a legacy numeric convenience that mirrors `valueDouble ??
 * valueInt` for callers that only care about numeric aggregates — it is
 * `null` for non-numeric tag types.
 */
export interface TagReading {
  time: string
  gatewayUrl?: string
  project?: string
  targetId?: string
  nodeId?: string
  tagPath: string
  tagType: string
  valueDouble: number | null
  valueInt: number | null
  valueBool: boolean | null
  valueString: string | null
  valueTimestamp: string | null
  valueJson: unknown | null
  quality: string
  isGood: boolean
  /** Convenience numeric projection (null for non-numeric tags). */
  value: number | null
}

export interface CreateTargetRequest {
  nodeId: string
  tagPath: string
  label: string
  sourceType: string
  connectionUrl: string
  project?: string
}
